import { useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, Stack, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import type { SavedFilter } from './types';
import FilterBuilderDialog, { type FilterModel } from './FilterBuilderDialog';

interface Props {
  open: boolean;
  filters: SavedFilter[];
  columns: string[];
  numericColumns: Set<string>;
  distinctValues: Map<string, string[]>;
  onClose: () => void;
  /** Emits the full next filter set on any add / edit / toggle / delete. */
  onChange: (filters: SavedFilter[]) => void;
}

// Seed a builder model from a saved filter (or a blank one for a new filter).
function toModel(f: SavedFilter | null): FilterModel {
  if (!f) {
    return { name: '', mode: 'builder', conditions: [], combinator: 'and', expression: '' };
  }
  return {
    name: f.name,
    mode: f.mode ?? (f.conditions?.length ? 'builder' : 'expression'),
    conditions: f.conditions ?? [],
    combinator: f.combinator ?? 'and',
    expression: f.expression,
  };
}

export default function FiltersManagerDialog({
  open, filters, columns, numericColumns, distinctValues, onClose, onChange,
}: Props) {
  // null = closed; -1 = adding new; >=0 = editing that index.
  const [builderIndex, setBuilderIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    onChange(filters.map((f, idx) => (idx === i ? { ...f, enabled: !f.enabled } : f)));
  };
  const remove = (i: number) => {
    onChange(filters.filter((_, idx) => idx !== i));
  };
  const applyFromBuilder = (model: FilterModel) => {
    const next: SavedFilter = {
      name: model.name,
      enabled: builderIndex != null && builderIndex >= 0 ? filters[builderIndex].enabled : true,
      expression: model.expression,
      mode: model.mode,
      conditions: model.conditions,
      combinator: model.combinator,
    };
    if (builderIndex != null && builderIndex >= 0) {
      onChange(filters.map((f, idx) => (idx === builderIndex ? next : f)));
    } else {
      onChange([...filters, next]);
    }
    setBuilderIndex(null);
  };

  // Names excluded from the duplicate-name check (the one being edited keeps its own).
  const otherNames = builderIndex != null && builderIndex >= 0
    ? filters.filter((_, idx) => idx !== builderIndex).map((f) => f.name)
    : filters.map((f) => f.name);

  const activeCount = filters.filter((f) => f.enabled && f.expression.trim()).length;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          Filters
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Rows must match <strong>all</strong> enabled filters.
            {filters.length > 0 && ` ${activeCount} of ${filters.length} enabled.`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {filters.length === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 3, color: 'text.secondary' }}>
              <FilterAltOffIcon sx={{ fontSize: 41, opacity: 0.4 }} />
              <Typography variant="body2">No filters yet.</Typography>
              <Typography variant="caption">
                Add a named filter to narrow the previewed rows.
              </Typography>
            </Stack>
          ) : (
            <List dense disablePadding>
              {filters.map((f, i) => {
                const broken = !f.expression.trim();
                return (
                  <ListItem
                    key={`${f.name}-${i}`}
                    disableGutters
                    sx={{
                      borderBottom: 1, borderColor: 'divider', py: 0.5, gap: 0.5,
                      alignItems: 'flex-start',
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={0}>
                        <Tooltip title="Edit filter">
                          <IconButton size="small" edge="end" onClick={() => setBuilderIndex(i)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete filter">
                          <IconButton size="small" edge="end" onClick={() => remove(i)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <Tooltip title={f.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}>
                      <Checkbox
                        size="small"
                        checked={f.enabled}
                        onChange={() => toggle(i)}
                        sx={{ mt: -0.25 }}
                      />
                    </Tooltip>
                    <Box sx={{ minWidth: 0, flex: 1, pr: 6 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, opacity: f.enabled ? 1 : 0.55 }}
                        noWrap
                      >
                        {f.name || '(unnamed)'}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={broken ? 'error' : 'default'}
                        label={broken ? '(empty expression)' : f.expression}
                        title={f.expression}
                        sx={{
                          mt: 0.25, maxWidth: '100%', fontFamily: 'monospace',
                          opacity: f.enabled ? 1 : 0.55,
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                      />
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button startIcon={<AddIcon />} onClick={() => setBuilderIndex(-1)} sx={{ mr: 'auto' }}>
            Add filter
          </Button>
          <Button onClick={onClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {builderIndex != null && (
        <FilterBuilderDialog
          open
          columns={columns}
          numericColumns={numericColumns}
          distinctValues={distinctValues}
          isEdit={builderIndex >= 0}
          existingNames={otherNames}
          initial={toModel(builderIndex >= 0 ? filters[builderIndex] : null)}
          onClose={() => setBuilderIndex(null)}
          onApply={applyFromBuilder}
        />
      )}
    </>
  );
}
